import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Divider,
  Modal,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { STANDARD_MODAL_SURFACE_SX } from "../../utils/formModalStyles";

const modalStyle = {
  ...STANDARD_MODAL_SURFACE_SX,
  width: "90%",
  maxWidth: 600,
  p: 4,
  borderRadius: 3,
};

export default function Checkout({ open, onClose }) {
  const [method, setMethod] = useState("credit");
  const [insuranceError, setInsuranceError] = useState("");
  const [card, setCard] = useState({
    name: "",
    number: "",
    expiry: "",
    cvv: "",
  });
  const [upiId, setUpiId] = useState("");
  const [insuranceId, setInsuranceId] = useState("");
  const [selectedUpiApp, setSelectedUpiApp] = useState("");
  const [upiErrors, setUpiErrors] = useState({
    app: "",
    upiId: "",
  });
  const [cardErrors, setCardErrors] = useState({
    name: "",
    number: "",
    expiry: "",
    cvv: "",
  });

  const validateCard = () => {
    const errors = {};

    if (!card.name.trim()) {
      errors.name = "Cardholder name is required";
    }

    if (!/^\d{16}$/.test(card.number)) {
      errors.number = "Card number must be 16 digits";
    }

    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(card.expiry)) {
      errors.expiry = "Expiry must be in MM/YY format";
    }

    if (!/^\d{3}$/.test(card.cvv)) {
      errors.cvv = "CVV must be 3 digits";
    }

    setCardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateUpi = () => {
    const errors = {};
    if (!selectedUpiApp) {
      errors.app = "Please select a UPI app";
    }
    if (!upiId.trim()) {
      errors.upiId = "Please enter UPI ID";
    }
    setUpiErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateInsurance = () => {
    if (!insuranceId.trim()) {
      setInsuranceError("Please enter Insurance Policy Number");
      return false;
    }
    setInsuranceError("");
    return true;
  };

  const handlePayment = () => {
    if ((method === "credit" || method === "debit") && !validateCard()) {
      return;
    } else if (method === "upi") {
      if (!validateUpi()) return;
    } else if (method === "insurance") {
      if (!validateInsurance()) return;
    }
    alert(`Processing payment via ${method}`);
    onClose();
  };

  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric
    if (value.length >= 3) {
      value = value.slice(0, 2) + "/" + value.slice(2, 4);
    }
    setCard({ ...card, expiry: value });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" fontWeight="bold" align="center" gutterBottom>
          Payment
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Dropdown for Payment Method */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Payment Method</InputLabel>
          <Select
            value={method}
            label="Select Payment Method"
            onChange={(e) => setMethod(e.target.value)}
          >
            <MenuItem value="credit">Credit Card</MenuItem>
            <MenuItem value="debit">Debit Card</MenuItem>
            <MenuItem value="upi">UPI</MenuItem>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="insurance">Health Insurance</MenuItem>
          </Select>
        </FormControl>

        {/* Credit or Debit Card Form */}
        {(method === "credit" || method === "debit") && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {method === "credit" ? "Credit" : "Debit"} Card Details
            </Typography>
            <TextField
              label="Cardholder Name"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              value={card.name}
              onChange={(e) => setCard({ ...card, name: e.target.value })}
              error={!!cardErrors.name}
              helperText={cardErrors.name}
            />
            <TextField
              label="Card Number"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              value={card.number}
              onChange={(e) => setCard({ ...card, number: e.target.value })}
              error={!!cardErrors.number}
              helperText={cardErrors.number}
            />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  label="Expiry (MM/YY)"
                  fullWidth
                  value={card.expiry}
                  onChange={handleExpiryChange}
                  error={!!cardErrors.expiry}
                  helperText={cardErrors.expiry}
                  inputProps={{ maxLength: 5 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="CVV"
                  type="password"
                  fullWidth
                  value={card.cvv}
                  onChange={(e) => setCard({ ...card, cvv: e.target.value })}
                  error={!!cardErrors.cvv}
                  helperText={cardErrors.cvv}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* UPI Section */}
        {method === "upi" && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              UPI Payment
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {[
                { id: "gpay", name: "Google Pay", src: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT0K7UR8kQj5FiKOQmorJ_8D7IXXNe12UeDpPAUHHFfUzC5COqaifJZA47ILgiawNZUOTQ&usqp=CAU" },
                { id: "phonepe", name: "PhonePe", src: "https://download.logo.wine/logo/PhonePe/PhonePe-Logo.wine.png" },
                { id: "paytm", name: "Paytm", src: "https://cdn-icons-png.flaticon.com/512/825/825454.png" },
              ].map((app) => (
                <Grid item xs={4} key={app.id}>
                  <Box
                    onClick={() => {
                      setSelectedUpiApp(app.id);
                      setUpiErrors((prev) => ({ ...prev, app: "" }));
                    }}
                    sx={{
                      cursor: "pointer",
                      border: selectedUpiApp === app.id ? "2px solid #1976d2" : "1px solid #ccc",
                      borderRadius: 2,
                      p: 1,
                      textAlign: "center",
                      backgroundColor: selectedUpiApp === app.id ? "#e3f2fd" : "#fff",
                    }}
                  >
                    <img
                      src={app.src}
                      alt={app.name}
                      style={{ width: "100%", height: "40px", objectFit: "contain" }}
                    />
                    <Typography variant="caption">{app.name}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <TextField
              label="Enter UPI ID"
              variant="outlined"
              fullWidth
              value={upiId}
              onChange={(e) => {
                setUpiId(e.target.value);
                setUpiErrors((prev) => ({ ...prev, upiId: "" }));
              }}
              error={!!upiErrors.upiId}
              helperText={upiErrors.upiId}
            />
          </>
        )}

        {/* Cash Message */}
        {method === "cash" && (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            You have selected to pay by <strong>Cash</strong> at the hospital.
          </Typography>
        )}

        { /* Health Insurance */  }
        {method === "insurance" && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Enter Insurance Details
            </Typography>
            <TextField
              label="Insurance Policy Number"
              fullWidth
              variant="outlined"
              value={insuranceId}
              onChange={(e) => {
                setInsuranceId(e.target.value);
                setInsuranceError("");
              }}
              error={!!insuranceError}
              helperText={insuranceError}
            />
          </>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 4 }}
          onClick={handlePayment}
        >
          Pay Now
        </Button>
      </Box>
    </Modal>
  );
}